import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useIssuerPortfolioBreakdownChartByIdQuery } from '@/modules/issuer-dashboard/queries/portfolios.tsx';
import { BalanceChart, ChartEmptyState, InfoCard, Spinner } from 'ui';
import { ApexChartSeries, ChartSeriesDataProps } from 'ui/types';
import { normaliseChartLineData } from 'ui/utils';
import { OvertimeData } from 'api';

interface ChartAssetsBreakdownProps {
  portfolioId: number;
}

export const ChartAssetsBreakdown = ({ portfolioId }: ChartAssetsBreakdownProps) => {
  const {
    isFetching,
    isLoading,
    isError,
    data: dataBreakdownChart,
  } = useIssuerPortfolioBreakdownChartByIdQuery({
    portfolio_id: portfolioId,
  });

  const chartData = useMemo<ApexChartSeries[]>(() => {
    if (!dataBreakdownChart?.data?.length) return [];

    const items = dataBreakdownChart?.data?.map<ApexChartSeries>(item => ({
      name: item.name,
      type: 'line',
      currency: item?.currency?.toUpperCase() || 'AUD',
      data: item.data?.map<ChartSeriesDataProps>((point: OvertimeData) => ({
        x: point.date,
        y: point.value,
      })),
    }));

    return normaliseChartLineData(items);
  }, [dataBreakdownChart?.data]);

  return (
    <InfoCard
      title="Assets breakdown"
      classes={{ content: 'w-full p-0 px-6 pt-4' }}
      headerAction={
        <div className="flex items-center justify-end gap-2">
          {isFetching && !isLoading && <Spinner size="md" />}
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        {!chartData?.length && (
          <div className="py-4 flex justify-center items-center font-medium text-lg">
            <ChartEmptyState isError={isError} isLoading={isLoading} />
          </div>
        )}
        {!isLoading && chartData?.length > 0 && (
          <BalanceChart
            height="370"
            fill={false}
            type="line"
            data={chartData}
            colors={['#0369A1', '#0EA5E9', '#10B981', '#F59E0B', '#A855F7', '#ABABAB']}
            options={{
              markers: {
                size: 5,
                hover: { size: 7 },
              },
              xaxis: {
                tickAmount: 6,
                axisTicks: {
                  show: false,
                },
                axisBorder: {
                  show: true,
                  // strokeWidth: 1,
                  color: '#E5E7EB',
                  offsetY: -1,
                },
                labels: {
                  datetimeUTC: true,
                  showDuplicates: false,
                  rotate: 0,
                  hideOverlappingLabels: false,
                  trim: true,
                  style: {
                    colors: '#A3A3A3',
                    fontFamily: 'Inter',
                    fontWeight: 400,
                    fontSize: '12px',
                    cssClass: '!font-normal',
                  },
                  formatter: (value: string) => {
                    return dayjs(value).isValid() ? dayjs(value).format('MMM YY') : value;
                  },
                },
                tooltip: {
                  enabled: false,
                },
              },
            }}
          />
        )}
      </div>
    </InfoCard>
  );
};
